import React, { useState, useEffect } from 'react'
import useSWR from 'swr'
import fetcher from '@lib/fetcher'
import OpeningStatus from './opening-status'
import InstancesList from './instances-list'

const KioskDisplay = ({ placeName, testDate, simulatedNow }) => {
  // Calculate time offset if using test mode
  const initialSimulatedTime = new Date(simulatedNow || new Date())
  const initialRealTime = new Date()
  const timeOffset = initialSimulatedTime - initialRealTime

  const [currentTime, setCurrentTime] = useState(initialSimulatedTime)

  // Parse the test date to format it for API calls
  const displayDate = testDate || new Date().toISOString().split('T')[0]

  // Fetch opening times data with robust error handling
  const { data: openingTimes, error: openingError } = useSWR(
    `/opening_times/${displayDate}`,
    fetcher,
    {
      refreshInterval: 30000, // Refresh every 30 seconds
      dedupingInterval: 5000,
      errorRetryInterval: 5000,
      errorRetryCount: 10,
      shouldRetryOnError: true,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      fetcher: (url) => fetch(url, { timeout: 10000 }).then(res => res.json())
    }
  )

  // Fetch instances for the date with robust error handling
  const { data: instances, error: instancesError, isLoading: instancesLoading } = useSWR(
    `/whatson/${displayDate}`,
    fetcher,
    {
      refreshInterval: 30000, // Refresh every 30 seconds
      dedupingInterval: 5000,
      errorRetryInterval: 5000,
      errorRetryCount: 10,
      shouldRetryOnError: true,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      fetcher: (url) => fetch(url, { timeout: 10000 }).then(res => res.json())
    }
  )

  // Watchdog: Reload page if no data after 2 minutes (prevents indefinite freeze)
  useEffect(() => {
    const watchdog = setTimeout(() => {
      if (!instances && !openingTimes) {
        console.log('Kiosk watchdog: No data received, reloading page...')
        window.location.reload()
      }
    }, 120000) // 2 minutes

    return () => clearTimeout(watchdog)
  }, [instances, openingTimes])

  // Auto-reload page every 24 hours to prevent memory leaks
  useEffect(() => {
    const autoReload = setTimeout(() => {
      console.log('Kiosk auto-reload: 24 hours elapsed, refreshing...')
      window.location.reload()
    }, 24 * 60 * 60 * 1000) // 24 hours

    return () => clearTimeout(autoReload)
  }, [])

  // Handle visibility changes - ensure clock keeps running
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Tab became visible again, sync time
        const now = new Date()
        const adjustedTime = new Date(now.getTime() + timeOffset)
        setCurrentTime(adjustedTime)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [timeOffset])

  // Update current time every second, maintaining test date offset if present
  // Using both setInterval AND requestAnimationFrame for reliability
  useEffect(() => {
    let frameId
    let lastUpdate = Date.now()

    const updateTime = () => {
      const now = new Date()
      const adjustedTime = new Date(now.getTime() + timeOffset)
      setCurrentTime(adjustedTime)
      lastUpdate = Date.now()
    }

    // Primary timer - updates every second
    const timer = setInterval(updateTime, 1000)

    // Backup animation frame loop - checks if timer is working
    const checkAndUpdate = () => {
      const timeSinceLastUpdate = Date.now() - lastUpdate

      // If more than 2 seconds since last update, force update
      if (timeSinceLastUpdate > 2000) {
        console.log('Clock update delayed, forcing update...')
        updateTime()
      }

      frameId = requestAnimationFrame(checkAndUpdate)
    }

    frameId = requestAnimationFrame(checkAndUpdate)

    return () => {
      clearInterval(timer)
      cancelAnimationFrame(frameId)
    }
  }, [timeOffset])

  // Filter instances to only show from active venues (keep all time periods)
  const filteredInstances = instances?.filter(instance => {
    return instance.venue?.active !== false
  }) || []

  // Loading state
  if (!openingTimes && !instances) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-4xl text-gray-400">Loading...</div>
      </div>
    )
  }

  // Error state - show last known good data if available, otherwise show error
  if (openingError || instancesError) {
    if (!instances && !openingTimes) {
      return (
        <div className="h-full flex items-center justify-center">
          <div className="text-center">
            <div className="text-4xl text-red-500 mb-4">Connection Error</div>
            <div className="text-2xl text-gray-400">Retrying...</div>
          </div>
        </div>
      )
    }
    // If we have data, continue showing it despite errors
  }

  return (
    <div className="h-full flex flex-col bg-black text-white">
      {/* Opening status strip */}
      <div className="border-b border-gray-700">
        <OpeningStatus
          placeName={placeName}
          openingTimes={openingTimes}
          currentTime={currentTime}
        />
      </div>

      {/* Instances list */}
      <div className="flex-1 overflow-y-auto">
        <InstancesList
          instances={filteredInstances}
          currentTime={currentTime}
          isLoading={instancesLoading}
        />
      </div>
    </div>
  )
}

export default KioskDisplay
